import { useState, useEffect } from 'react';
import {
  IconChevronDownFilled,
  IconReceiptFilled,
  IconBoxMultipleFilled,
  IconSunFilled,
  IconCalendarFilled,
  IconCreditCardFilled,
  IconDiscountFilled,
  IconShoppingCartFilled,
  IconArchiveFilled,
  IconTruckFilled,
  IconCircleCheckFilled,
  IconCircleXFilled,
  IconSearchFilled,
  IconAlertTriangleFilled,
  IconRefresh,
} from '@tabler/icons-react';
import Busqueda from '../components/Busqueda';
import TablaRegistroVentas from '../components/TablaRegistroVentas';
import BadgedMetricas from '../Utils/BadgedMetricas';
import ModalDetallesPedido from '../components/ModalDetallesPedido';
import ModalEliminarVenta from '../components/ModalEliminarVenta';
import AgujeroNegro from '../components/AgujeroNegro';
import DeleteIconCircle from '../components/DeleteIconCircle';
import DetallesBlue from '../Utils/DetallesBlue';
import TipBlue from '../Utils/TipBlue';
import { ensureMinLoading } from '../Utils/withLoading';
import { apiFetch, apiJson, apiUrl, getSession } from '../api/client';

const FONT = '"Public Sans", sans-serif';
const POLL_INTERVAL_MS = 10000;

const TYPE_LABELS = {
  in_store: 'En tienda',
  pickup: 'Pickup',
  delivery: 'Domicilio',
};

const METHOD_LABELS = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
  terminal: 'Terminal',
  debit: 'Débito',
  credit: 'Crédito',
  points: 'Puntos',
  mixed: 'Mixto',
  pending: 'Pendiente',
};

const STATUS_LABELS = {
  ready: 'Listo',
  completed: 'Completado',
  cancelled: 'Cancelado',
};

const STATUS_BADGE_CONFIG = {
  ready: { icon: IconCircleCheckFilled, color: '#30d158' },
  completed: { icon: IconCircleCheckFilled, color: '#30d158' },
  cancelled: { icon: IconCircleXFilled, color: '#f87c2b' },
};

const TYPE_BADGES = [
  { key: 'in_store', icon: IconShoppingCartFilled, color: '#0a84ff' },
  { key: 'pickup', icon: IconArchiveFilled, color: '#8e8e93' },
  { key: 'delivery', icon: IconTruckFilled, color: '#30d158' },
];

const MUTED = 'rgba(255, 255, 255, 0.3)';
const AMBER = '#f87c2b';
const GREEN = '#30d158';

function formatPrice(value) {
  return '$' + (Number(value) || 0).toFixed(2);
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('es-MX');
}

function hexToRgba(hex, alpha) {
  const h = String(hex || '#8e8e93').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

const CLOSED_STATUSES = ['ready', 'completed', 'cancelled'];

function buildMonthOptions() {
  return Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i, 1).toLocaleDateString('es-MX', { month: 'long' }),
  }));
}

function buildYearOptions() {
  const now = new Date();
  const options = [];
  for (let y = now.getFullYear() - 5; y <= now.getFullYear(); y++) {
    options.push({ value: y, label: String(y) });
  }
  return options.reverse();
}

function MetricCard({ icon: Icon, color, label, value }) {
  return (
    <div
      className="flex flex-col gap-3 rounded-3xl min-w-0"
      style={{ backgroundColor: 'rgba(255, 255, 255, 0.035)', padding: '14px 16px' }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="flex items-center justify-center shrink-0 w-8 h-8 rounded-full"
          style={{ backgroundColor: hexToRgba(color, 0.14) }}
        >
          <Icon size={16} color={color} />
        </span>
        <span
          className="truncate"
          style={{
            fontSize: '11px',
            lineHeight: '14px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.35)',
            fontFamily: FONT,
          }}
        >
          {label}
        </span>
      </div>
      <span
        className="truncate"
        title={String(value)}
        style={{
          fontSize: '20px',
          lineHeight: '24px',
          fontWeight: 600,
          letterSpacing: '-0.02em',
          color: 'rgba(255, 255, 255, 0.95)',
          fontFamily: FONT,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function PeriodoSelect({ label, value, onChange, options, minWidth }) {
  return (
    <div className="relative shrink-0">
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="appearance-none outline-none cursor-pointer touch-manipulation border-none text-white"
        style={{
          height: '48px',
          borderRadius: '9999px',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          padding: '0 44px 0 18px',
          minWidth,
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          fontFamily: FONT,
          color: 'rgba(255, 255, 255, 0.9)',
          WebkitAppearance: 'none',
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>
            {o.label}
          </option>
        ))}
      </select>
      <span
        className="flex items-center justify-center pointer-events-none absolute w-6 h-6 rounded-full"
        style={{
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
        }}
      >
        <IconChevronDownFilled size={12} color="rgba(255, 255, 255, 0.7)" />
      </span>
    </div>
  );
}

function TablaSkeleton() {
  return (
    <div className="flex flex-col gap-1.5" role="status" aria-label="Cargando ventas">
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <div
          key={i}
          className="flex items-center gap-4 rounded-2xl animate-pulse"
          style={{ height: '64px', padding: '0 12px' }}
        >
          <span
            className="shrink-0 rounded-full"
            style={{ width: '22px', height: '22px', backgroundColor: '#141414' }}
          />
          <span
            className="rounded-full"
            style={{ width: '22%', height: '12px', backgroundColor: '#141414' }}
          />
          <span
            className="rounded-full"
            style={{ width: '14%', height: '12px', backgroundColor: '#141414' }}
          />
          <span
            className="rounded-full"
            style={{ width: '10%', height: '12px', backgroundColor: '#141414' }}
          />
          <span
            className="ml-auto rounded-full"
            style={{ width: '9%', height: '12px', backgroundColor: '#141414' }}
          />
        </div>
      ))}
    </div>
  );
}

function SinResultados({ query, onClear }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <span
        className="flex items-center justify-center w-11 h-11 rounded-2xl"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.035)' }}
      >
        <IconSearchFilled size={21} color="rgba(255, 255, 255, 0.3)" />
      </span>
      <div>
        <p
          className="m-0"
          style={{
            fontSize: '15px',
            lineHeight: '20px',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: FONT,
          }}
        >
          Sin resultados
        </p>
        <p
          className="m-0 mt-1"
          style={{
            fontSize: '13px',
            lineHeight: '19px',
            fontWeight: 500,
            color: 'rgba(255, 255, 255, 0.4)',
            fontFamily: FONT,
          }}
        >
          No encontramos ventas para «{query}»
        </p>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="flex items-center justify-center h-10 px-4 mt-1 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.97] [@media(hover:hover)]:hover:bg-white/[0.1]"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'rgba(255, 255, 255, 0.88)',
          fontFamily: FONT,
        }}
      >
        Limpiar búsqueda
      </button>
    </div>
  );
}

function ErrorCarga({ onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <span
        className="flex items-center justify-center w-11 h-11 rounded-2xl"
        style={{ backgroundColor: 'rgba(255, 69, 58, 0.1)' }}
      >
        <IconAlertTriangleFilled size={20} color="#ff453a" />
      </span>
      <div>
        <p
          className="m-0"
          style={{
            fontSize: '15px',
            lineHeight: '20px',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: FONT,
          }}
        >
          No pudimos cargar las ventas
        </p>
        <p
          className="m-0 mt-1"
          style={{
            fontSize: '13px',
            lineHeight: '19px',
            fontWeight: 500,
            color: 'rgba(255, 255, 255, 0.4)',
            fontFamily: FONT,
          }}
        >
          Revisa la conexión e inténtalo de nuevo
        </p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="flex items-center justify-center gap-1.5 h-11 px-4 mt-1 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.97] [@media(hover:hover)]:hover:bg-white/[0.1]"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'rgba(255, 255, 255, 0.9)',
          fontFamily: FONT,
        }}
      >
        <IconRefresh size={15} color="rgba(255, 255, 255, 0.6)" />
        Reintentar
      </button>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE_CONFIG[status] || STATUS_BADGE_CONFIG.completed;
  const StatusIcon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
        borderRadius: '9999px',
        height: '30px',
        padding: '0 12px',
        whiteSpace: 'nowrap',
        fontFamily: FONT,
      }}
    >
      <StatusIcon size={13} color={cfg.color} />
      <span
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'rgba(255, 255, 255, 0.8)',
        }}
      >
        {STATUS_LABELS[status] || status}
      </span>
    </span>
  );
}

export default function RegistroVentas({ notify }) {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    return new Date().getMonth() + 1;
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    return new Date().getFullYear();
  });
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [detailsSaleId, setDetailsSaleId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rowsToDelete, setRowsToDelete] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [search, setSearch] = useState('');

  const monthValue = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
  const dateFrom = `${monthValue}-01`;
  const nextMonthDate = new Date(selectedYear, selectedMonth, 1);
  const dateTo = `${nextMonthDate.getFullYear()}-${String(nextMonthDate.getMonth() + 1).padStart(2, '0')}-01`;

  useEffect(() => {
    let stopped = false;
    let timer = null;
    let controller = null;
    let firstRun = true;
    let lastUpdatedAt = null;
    let pollCount = 0;
    const FULL_RECONCILE_POLLS = 6;

    const fetchClosedSales = async (updatedAfter) => {
      let query =
        `/sales?date_from=${dateFrom}&date_to=${dateTo}` +
        `&status=${CLOSED_STATUSES.join(',')}&limit=1000`;
      if (updatedAfter) {
        query += `&updated_after=${encodeURIComponent(updatedAfter)}`;
      }
      // apiJson y no fetch: es lo que añade la cabecera con la API key.
      const data = await apiJson(query, { signal: controller.signal });
      return data.sales || [];
    };

    const maxUpdatedAt = (list, fallback) =>
      list.reduce((acc, s) => (s.updated_at > acc ? s.updated_at : acc), fallback);

    const applyFull = (list) => {
      setSales(list);
      lastUpdatedAt = maxUpdatedAt(list, lastUpdatedAt);
    };

    const applyDelta = (list) => {
      setSales((prev) => {
        const map = new Map(prev.map((s) => [s.id, s]));
        for (const s of list) {
          if (CLOSED_STATUSES.includes(s.status)) map.set(s.id, s);
          else map.delete(s.id);
        }
        return Array.from(map.values());
      });
      lastUpdatedAt = maxUpdatedAt(list, lastUpdatedAt);
    };

    const load = async (showSpinner, useDelta) => {
      controller = new AbortController();
      if (showSpinner) setLoading(true);
      try {
        if (document.hidden) return;
        const list = await fetchClosedSales(useDelta && lastUpdatedAt ? lastUpdatedAt : null);
        if (stopped) return;
        if (useDelta && lastUpdatedAt) applyDelta(list);
        else applyFull(list);
        setLoadError(false);
      } catch {
        if (!stopped) setLoadError(true);
      } finally {
        if (!stopped && showSpinner) setLoading(false);
      }
    };

    const loop = async () => {
      if (stopped) return;
      pollCount += 1;
      const useDelta = !firstRun && pollCount % FULL_RECONCILE_POLLS !== 0;
      await load(firstRun, useDelta);
      firstRun = false;
      if (stopped) return;
      timer = setTimeout(loop, POLL_INTERVAL_MS);
    };

    loop();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      if (controller) controller.abort();
    };
  }, [dateFrom, dateTo, refreshKey]);

  // Badges vía SSE: el backend calcula todo y empuja solo cuando cambian.
  useEffect(() => {
    let stopped = false;
    let source = null;
    let reconnectTimer = null;

    // Al cambiar el periodo, no mostrar las métricas del mes anterior.
    setMetrics(null);

    const connect = () => {
      // EventSource no admite cabeceras, así que la API key viaja como query
      // param. Es el único endpoint del proyecto que la acepta: el resto exige
      // la cabecera para no dejarla escrita en los logs del servidor.
      const session = getSession();
      const key = session && session.apiKey ? `&api_key=${encodeURIComponent(session.apiKey)}` : '';
      const url = apiUrl(
        `/sales/metrics/stream?date_from=${dateFrom}&date_to=${dateTo}${key}`
      );
      source = new EventSource(url);
      source.onmessage = (event) => {
        if (stopped) return;
        try {
          setMetrics(JSON.parse(event.data));
        } catch {}
      };
      source.onerror = () => {
        // Errores transitorios: EventSource reintenta solo. Si cierra, reconectamos.
        if (!stopped && source && source.readyState === EventSource.CLOSED) {
          reconnectTimer = setTimeout(connect, 3000);
        }
      };
    };

    connect();
    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      if (source) source.close();
    };
  }, [dateFrom, dateTo]);

  const openDeleteModal = (selectedRows) => {
    setRowsToDelete(selectedRows || []);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!rowsToDelete.length) return;
    const ids = rowsToDelete.map((r) => r.sale_id);
    setDeleteModalOpen(false);
    setRowsToDelete([]);
    const startedAt = Date.now();
    notify?.('loading', `Eliminando ${ids.length} ${ids.length === 1 ? 'venta' : 'ventas'}...`);
    let failed = 0;
    for (const id of ids) {
      try {
        const res = await apiFetch(`/sales/${id}`, { method: 'DELETE' });
        if (!res.ok) failed++;
      } catch {
        failed++;
      }
    }
    await ensureMinLoading(startedAt);
    notify?.('clear');
    setRefreshKey((k) => k + 1);
    if (failed === 0) {
      notify?.('success', `${ids.length} ${ids.length === 1 ? 'venta eliminada' : 'ventas eliminadas'} correctamente`);
    } else if (failed < ids.length) {
      notify?.('error', `${failed} ${failed === 1 ? 'venta no pudo' : 'ventas no pudieron'} ser eliminadas`);
    } else {
      notify?.('error', 'No se pudieron eliminar las ventas. Si el problema persiste, reporta el error.');
    }
  };

  const m = metrics || {
    ventas: 0,
    articulos: 0,
    dia: 0,
    mes: 0,
    comision: 0,
    descuentos: 0,
    type_counts: {},
  };
  const typeCounts = m.type_counts || {};
  const hasMetrics = Boolean(metrics);

  const metricCards = [
    { key: 'ventas', label: 'Ventas', value: hasMetrics ? m.ventas : '—', icon: IconReceiptFilled, color: '#0a84ff' },
    { key: 'articulos', label: 'Artículos', value: hasMetrics ? m.articulos : '—', icon: IconBoxMultipleFilled, color: '#64d2ff' },
    { key: 'dia', label: 'Día', value: hasMetrics ? formatPrice(m.dia) : '—', icon: IconSunFilled, color: '#30d158' },
    { key: 'mes', label: 'Mes', value: hasMetrics ? formatPrice(m.mes) : '—', icon: IconCalendarFilled, color: '#bf5af2' },
    { key: 'comision', label: 'Comisión', value: hasMetrics ? formatPrice(m.comision) : '—', icon: IconCreditCardFilled, color: '#ff9f0a' },
    { key: 'descuentos', label: 'Descuentos', value: hasMetrics ? formatPrice(m.descuentos) : '—', icon: IconDiscountFilled, color: '#ff453a' },
  ];

  const columns = [
    { key: 'id', label: 'ID', flex: 1, minWidth: '110px' },
    { key: 'cliente', label: 'Cliente', flex: 1.5, minWidth: '140px' },
    { key: 'articulos', label: 'Artículos', flex: 0.7, minWidth: '80px' },
    { key: 'tipo', label: 'Tipo', flex: 1, minWidth: '100px', badge: true },
    {
      key: 'estado',
      label: 'Estado',
      flex: 0.8,
      minWidth: '110px',
      render: (row) => <StatusBadge status={row._status} />,
    },
    {
      key: 'subtotal',
      label: 'Subtotal',
      flex: 0.8,
      minWidth: '92px',
      render: (row) => (
        <span style={row._cancelled ? { color: AMBER } : undefined}>{row.subtotal}</span>
      ),
    },
    {
      key: 'descuento',
      label: 'Descuento',
      flex: 0.8,
      minWidth: '96px',
      render: (row) => (
        <span style={{ color: row.descuento === '-' ? MUTED : AMBER }}>{row.descuento}</span>
      ),
    },
    {
      key: 'comision',
      label: 'Comisión',
      flex: 0.8,
      minWidth: '92px',
      render: (row) => (
        <span style={{ color: row.comision === '-' ? MUTED : AMBER }}>{row.comision}</span>
      ),
    },
    {
      key: 'iva',
      label: 'IVA',
      flex: 0.8,
      minWidth: '84px',
      render: (row) => (
        <span style={{ color: row.iva === '-' ? MUTED : AMBER }}>{row.iva}</span>
      ),
    },
    {
      key: 'total',
      label: 'Recibido',
      flex: 0.9,
      minWidth: '92px',
      render: (row) => (
        <span style={{ color: row._cancelled ? AMBER : GREEN }}>{row.total}</span>
      ),
    },
    { key: 'fecha', label: 'Fecha', flex: 0.8, minWidth: '92px' },
    {
      key: 'detalles',
      label: '',
      flex: 0,
      minWidth: '120px',
      render: (row) => <DetallesBlue onClick={() => setDetailsSaleId(row.sale_id)} />,
    },
    {
      key: 'eliminar',
      label: '',
      flex: 0,
      minWidth: '50px',
      render: (row) => (
        <DeleteIconCircle solid size={44} onClick={() => openDeleteModal([row])} />
      ),
    },
  ];

  const rows = sales.map((s) => {
    const isCancelled = s.status === 'cancelled';
    const comisionVal = Number(s.mp_commission_amount) || 0;
    const ivaVal = Number(s.tax_amount) || 0;
    const subtotalVal = Number(s.subtotal) || 0;
    const totalVal = Number(s.total_amount) || 0;
    const descuentoVal = Number(s.total_discount) || 0;
    return {
      id: s.code_order,
      sale_id: s.id,
      cliente: s.customer_name || 'Cliente mostrador',
      articulos: s.items_count,
      tipo: TYPE_LABELS[s.sale_type] || s.sale_type,
      metodo: METHOD_LABELS[s.payment_method] || s.payment_method,
      subtotal: isCancelled ? formatPrice(0) : formatPrice(subtotalVal),
      descuento: !isCancelled && descuentoVal > 0 ? `-${formatPrice(descuentoVal)}` : '-',
      comision: !isCancelled && comisionVal > 0 ? `-${formatPrice(comisionVal)}` : '-',
      iva: !isCancelled && ivaVal > 0 ? `-${formatPrice(ivaVal)}` : '-',
      total: isCancelled ? formatPrice(0) : formatPrice(totalVal),
      fecha: formatDate(s.sale_date),
      _status: s.status,
      _cancelled: isCancelled,
    };
  });

  const normalizedQuery = normalizeText(search.trim());
  const filteredRows = normalizedQuery
    ? rows.filter((row) =>
        [row.id, row.cliente, row.tipo, row.fecha, row.metodo].some((field) =>
          normalizeText(field).includes(normalizedQuery)
        )
      )
    : rows;

  const countLabel = normalizedQuery
    ? `${filteredRows.length} ${filteredRows.length === 1 ? 'resultado' : 'resultados'}`
    : `${rows.length} ${rows.length === 1 ? 'venta' : 'ventas'}`;

  const monthOptions = buildMonthOptions();
  const yearOptions = buildYearOptions();

  return (
    <div className="flex flex-col h-screen bg-black overflow-hidden">
      <header className="shrink-0 px-4 lg:px-6 pt-5 pb-4 border-b border-white/[0.16]">
        <div className="min-w-0">
          <h1
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            Registro de ventas
          </h1>
          <p
            className="m-0 mt-1"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
              fontFamily: FONT,
            }}
          >
            Historial y detalle de ventas
          </p>
        </div>
      </header>

      <div className="flex flex-col flex-1 min-h-0 px-4 lg:px-6 pt-5 pb-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 shrink-0">
          {metricCards.map((card) => (
            <MetricCard
              key={card.key}
              icon={card.icon}
              color={card.color}
              label={card.label}
              value={card.value}
            />
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap shrink-0 pt-3">
          <span
            className="hidden sm:inline shrink-0"
            style={{
              fontSize: '11px',
              lineHeight: '14px',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'rgba(255, 255, 255, 0.35)',
              fontFamily: FONT,
            }}
          >
            Por tipo
          </span>
          {TYPE_BADGES.map(({ key, icon, color }) => (
            <BadgedMetricas
              key={key}
              color={color}
              label={TYPE_LABELS[key]}
              value={hasMetrics ? typeCounts[key] || 0 : '—'}
              icon={icon}
            />
          ))}
        </div>

        <div className="flex items-center gap-3 shrink-0 pt-4 pb-4">
          <div className="flex-1 max-w-[560px]">
            <Busqueda
              size="lg"
              value={search}
              onChange={setSearch}
              placeholder="Buscar venta, cliente o tipo"
            />
          </div>
          <span
            className="ml-auto shrink-0 hidden sm:inline"
            style={{
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.35)',
              fontFamily: FONT,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {loading ? 'Cargando…' : countLabel}
          </span>
        </div>

        <div className="flex-1 min-h-0" aria-busy={loading}>
          {loading ? (
            <div className="h-full overflow-y-auto pedidos-scroll">
              <TablaSkeleton />
            </div>
          ) : (
            <TablaRegistroVentas
              columns={columns}
              rows={filteredRows}
              selectable
              onDetails={(saleId) => setDetailsSaleId(saleId)}
              onDelete={openDeleteModal}
              emptyComponent={
                normalizedQuery ? (
                  <SinResultados query={search.trim()} onClear={() => setSearch('')} />
                ) : loadError ? (
                  <ErrorCarga onRetry={() => setRefreshKey((k) => k + 1)} />
                ) : (
                  <AgujeroNegro message="No hay ventas cerradas en este mes. Tus registros aparecerán aquí." />
                )
              }
            />
          )}
        </div>
      </div>

      <div className="shrink-0 px-4 lg:px-6 py-4 border-t border-white/[0.16] flex items-center justify-between gap-4">
        <TipBlue>Usa los selectores de mes y año para filtrar el historial de ventas.</TipBlue>
        <div className="flex items-center gap-2 shrink-0">
          <PeriodoSelect
            label="Mes"
            value={selectedMonth}
            onChange={setSelectedMonth}
            options={monthOptions}
            minWidth="150px"
          />
          <PeriodoSelect
            label="Año"
            value={selectedYear}
            onChange={setSelectedYear}
            options={yearOptions}
            minWidth="110px"
          />
        </div>
      </div>

      <ModalDetallesPedido
        isOpen={detailsSaleId !== null}
        saleId={detailsSaleId}
        onClose={() => setDetailsSaleId(null)}
      />
      <ModalEliminarVenta
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setRowsToDelete([]); }}
        onConfirm={handleDeleteConfirm}
        itemCount={rowsToDelete.length}
        codes={rowsToDelete.map((r) => r.id)}
      />
    </div>
  );
}
